// frontend/components/sidebar/DocPermissionModal.tsx

"use client";

import { useEffect, useState, useMemo } from "react";
import { X, Globe } from "lucide-react";
import { API_URL, authHeaders, authOnlyHeaders } from "../../utils/api";
import { ALL_DEPARTMENTS } from "../../constants/organization";
import PermSelect from "./docPermission/PermSelect";
import { TeamTab, UserTab, type EffectivePermission, type Perm4, type PermUserData } from "./docPermission/PermissionTabs";

interface DocPermissionModalProps {
  docId: string;
  docTitle: string;
  primaryDept: string;   // 생성팀
  currentUser: any;
  isGuestAllowed?: boolean;
  creatorName?: string | null;
  onClose: () => void;
}

type Tab = "team" | "user";
type GuestLevel = "NONE" | "READ" | "COMMENT";
type UserData = PermUserData;

const GUEST_OPTIONS: { value: GuestLevel; label: string }[] = [
  { value: "NONE",    label: "접근 불가" },
  { value: "READ",    label: "읽기 허용" },
  { value: "COMMENT", label: "댓글 허용" },
];


export default function DocPermissionModal({
  docId, docTitle, primaryDept, currentUser,
  isGuestAllowed = false, creatorName, onClose,
}: DocPermissionModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>("team");
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [teamPerms, setTeamPerms] = useState<Record<string, Perm4>>({});
  const [userPerms, setUserPerms] = useState<Record<string, Perm4>>({});
  const [guestLevel, setGuestLevel] = useState<GuestLevel>(isGuestAllowed ? "READ" : "NONE");
  const [permissionSource, setPermissionSource] = useState<{ id: string; title: string; inherited: boolean } | null>(null);
  const [effectivePermissions, setEffectivePermissions] = useState<Record<string, EffectivePermission>>({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/documents/${docId}/permissions`, {
          headers: authOnlyHeaders(currentUser?.accessToken),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error("[DocPermissionModal] 권한 조회 실패:", res.status, err);
          return;
        }
        const data = await res.json();
        const loadedPerms: any[] = data.permissions || [];
        const loadedUsers: UserData[] = data.users || [];
        setPermissionSource(data.source || null);
        setEffectivePermissions(Object.fromEntries((data.effectivePermissions || []).map((item: EffectivePermission & { userId: string }) => [item.userId, item])));

        // ADMIN·GUEST는 문서 권한 대상에서 제외
        const filteredUsers = loadedUsers.filter(
          (u: UserData) => u.role !== "ADMIN" && u.role !== "GUEST"
        );
        setUsers(filteredUsers);

        const creator = loadedUsers.find(
          u => u.name === creatorName || u.loginId === creatorName
        );

        // 팀 권한 초기화: 생성팀은 편집 허용 고정, 나머지는 저장값 혹은 권한 없음
        const tp: Record<string, Perm4> = {};
        for (const dept of ALL_DEPARTMENTS) {
          if (dept === primaryDept) {
            tp[dept] = "EDIT";
          } else {
            const found = loadedPerms.find(p => p.targetType === "DEPT" && p.targetId === dept);
            tp[dept] = found ? (found.level === "FULL" ? "EDIT" : (found.level as Perm4)) : "NONE";
          }
        }
        setTeamPerms(tp);

        // 유저 권한 초기화: 생성자는 FULL 고정(별도 처리), 팀원은 편집 허용, 나머지는 권한 없음
        const up: Record<string, Perm4> = {};
        for (const u of loadedUsers) {
          if (u.id === creator?.id) continue;
          const found = loadedPerms.find(p => p.targetType === "USER" && p.targetId === u.id);
          if (found) {
            up[u.id] = found.level === "FULL" ? "EDIT" : (found.level as Perm4);
          } else {
            up[u.id] = u.department === primaryDept ? "EDIT" : "NONE";
          }
        }
        setUserPerms(up);
      } catch (e) {
        console.error("[DocPermissionModal] 권한 조회 중 예외:", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [docId]);

  const creatorUser = useMemo(
    () => users.find(u => u.name === creatorName || u.loginId === creatorName) ?? null,
    [users, creatorName]
  );

  // 팀 목록: 생성팀 → 가나다순
  const sortedDepts = useMemo(() => {
    const rest = ALL_DEPARTMENTS
      .filter(d => d !== primaryDept)
      .sort((a, b) => a.localeCompare(b, "ko"));
    return [primaryDept, ...rest];
  }, [primaryDept]);

  // 유저 목록: 생성자 → 같은 팀 가나다순 → 나머지 가나다순
  const sortedUsers = useMemo(() => {
    const nonCreator = users.filter(u => u.id !== creatorUser?.id);
    const sameTeam = nonCreator
      .filter(u => u.department === primaryDept)
      .sort((a, b) => (a.name || a.loginId).localeCompare(b.name || b.loginId, "ko"));
    const others = nonCreator
      .filter(u => u.department !== primaryDept)
      .sort((a, b) => (a.name || a.loginId).localeCompare(b.name || b.loginId, "ko"));
    return [...(creatorUser ? [creatorUser] : []), ...sameTeam, ...others];
  }, [users, creatorUser, primaryDept]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const permissions: any[] = [];

      // 팀 권한 (NONE 제외)
      for (const [dept, level] of Object.entries(teamPerms)) {
        if (level !== "NONE") {
          permissions.push({ targetType: "DEPT", targetId: dept, level });
        }
      }

      // 생성자는 항상 전체허용
      if (creatorUser) {
        permissions.push({ targetType: "USER", targetId: creatorUser.id, level: "FULL" });
      }

      // 유저 권한 (NONE 제외)
      for (const [userId, level] of Object.entries(userPerms)) {
        if (level !== "NONE") {
          permissions.push({ targetType: "USER", targetId: userId, level });
        }
      }

      await fetch(`${API_URL}/documents/${docId}/permissions`, {
        method: "PUT",
        headers: authHeaders(currentUser.accessToken),
        body: JSON.stringify({ permissions, requesterId: currentUser.id }),
      });

      // 게스트 허용 여부 변경 시에만 PATCH
      const newGuestAllowed = guestLevel !== "NONE";
      if (newGuestAllowed !== isGuestAllowed) {
        await fetch(`${API_URL}/documents/${docId}`, {
          method: "PATCH",
          headers: authHeaders(currentUser.accessToken),
          body: JSON.stringify({
            isGuestAllowed: newGuestAllowed,
            requesterId: currentUser.id,
            requesterRole: currentUser.role,
            requesterDept: currentUser.department || "",
            userName: currentUser.name,
          }),
        });
      }

      window.dispatchEvent(new CustomEvent("force-sidebar-refresh"));
      onClose();
    } catch {
      alert("권한 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const TABS: { key: Tab; label: string }[] = [
    { key: "team", label: "팀" },
    { key: "user", label: "유저" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white dark:bg-[#202020] rounded-xl shadow-2xl w-130 max-h-[80vh] flex flex-col border border-gray-200 dark:border-zinc-700"
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-zinc-700 shrink-0">
          <div>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mb-0.5">권한 설정</p>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-zinc-100 truncate max-w-100">{docTitle}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-400 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Excel 시트 탭 */}
        {permissionSource?.inherited && (
          <a
            href={`/doc/${permissionSource.id}`}
            className="mx-5 mt-3 text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            상위 문서 〈{permissionSource.title || "제목 없음"}〉에서 권한 상속 · 변경은 상위 문서에서
          </a>
        )}
        <div className="flex gap-1 px-4 pt-2 bg-gray-100 dark:bg-zinc-800/60 border-b border-gray-200 dark:border-zinc-700 shrink-0">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{ borderRadius: "4px 4px 0 0" }}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                activeTab === key
                  ? "bg-white dark:bg-[#202020] border-t border-l border-r border-gray-200 dark:border-zinc-700 text-gray-800 dark:text-zinc-100 -mb-px relative z-10"
                  : "text-gray-500 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-700 hover:text-gray-700 dark:hover:text-zinc-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 탭 콘텐츠 */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-400">불러오는 중...</div>
          ) : activeTab === "team" ? (
            <TeamTab
              depts={sortedDepts}
              primaryDept={primaryDept}
              teamPerms={teamPerms}
              onChange={(dept, level) => setTeamPerms(prev => ({ ...prev, [dept]: level }))}
            />
          ) : (
            <UserTab
              users={sortedUsers}
              creatorUser={creatorUser}
              primaryDept={primaryDept}
              userPerms={userPerms}
              effectivePermissions={effectivePermissions}
              onChange={(userId, level) => setUserPerms(prev => ({ ...prev, [userId]: level }))}
            />
          )}
        </div>

        {/* 게스트 사용 권한 */}
        <div className="px-5 py-3 border-t border-gray-100 dark:border-zinc-800 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe size={13} className="text-gray-400 dark:text-zinc-500 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-zinc-200">게스트 사용 권한</p>
                <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">외부인(GUEST) 역할 사용자의 접근 수준</p>
              </div>
            </div>
            <PermSelect
              value={guestLevel}
              options={GUEST_OPTIONS}
              onChange={v => setGuestLevel(v as GuestLevel)}
            />
          </div>
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-zinc-700 shrink-0">
          <span className="text-xs text-gray-400 dark:text-zinc-500">
            {creatorName && (
              <>생성자: <span className="font-medium text-gray-600 dark:text-zinc-300">{creatorName}</span></>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || permissionSource?.inherited === true}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 rounded-lg transition-colors"
            >
              {permissionSource?.inherited ? "상속됨" : isSaving ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
